import { useDealsForHub } from '@/hooks/use-deals-selectors';
import usePerformantSingleHotKeysScope from '@/hooks/use-performant-single-hot-keys-scope';
import { HOTKEYS } from '@/lib/hotkeys';
import type { Deal } from '@/models/registry';

import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

export type HotkeyDialogBox =
  | 'priority'
  | 'contract-status'
  | 'status'
  | 'delivery-date'
  | 'sales-rep-1'
  | 'sales-rep-2'
  | 'finance-manager'
  | 'rapid-recon-step'
  | 'delivery-preference'
  | 'delivery-queue'
  | 'set-lender'
  | 'create-lender'
  | null;

const useAllListAndBoardHotkeys = () => {
  const deals = useDealsForHub();

  const { enableScope, singleEnabledScope } = usePerformantSingleHotKeysScope();

  const [openDialog, setOpenDialog] = useState<HotkeyDialogBox>(null);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);

  const DEAL_PRIORITY_HOTKEY = HOTKEYS.DEAL_PRIORITY as string;
  const DEAL_STATUS_HOTKEY = HOTKEYS.DEAL_STATUS as string;
  const DEAL_CONTRACT_STATUS_HOTKEY = HOTKEYS.DEAL_CONTRACT_STATUS as string;
  const DEAL_RAPID_RECON_STEP_HOTKEY = HOTKEYS.DEAL_RAPID_RECON_STEP as string;
  useHotkeys(
    [
      DEAL_PRIORITY_HOTKEY,
      DEAL_STATUS_HOTKEY,
      DEAL_CONTRACT_STATUS_HOTKEY,
      DEAL_RAPID_RECON_STEP_HOTKEY,
    ],
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const hotkeyScope = singleEnabledScope[0];
      const dealShortId = hotkeyScope.split('-deal-line-or-card')[0];
      const deal = deals?.find((deal) => deal.short_id === dealShortId) || null;
      setSelectedDeal(deal);

      switch (e.key) {
        case DEAL_PRIORITY_HOTKEY:
        case DEAL_PRIORITY_HOTKEY.toLowerCase():
          if (deal) {
            setOpenDialog('priority');
          }
          break;
        case DEAL_STATUS_HOTKEY:
        case DEAL_STATUS_HOTKEY.toLowerCase():
          if (deal) {
            setOpenDialog('status');
          }
          break;
        case DEAL_CONTRACT_STATUS_HOTKEY:
        case DEAL_CONTRACT_STATUS_HOTKEY.toLowerCase():
          if (deal) {
            setOpenDialog('contract-status');
          }
          break;
        case DEAL_RAPID_RECON_STEP_HOTKEY:
        case DEAL_RAPID_RECON_STEP_HOTKEY.toLowerCase():
          if (deal) {
            setOpenDialog('rapid-recon-step');
          }
          break;
      }
    },
  );

  useHotkeys(HOTKEYS.DEAL_DELIVERY_DATE, (e) => {
    // DEV NOTE: this has to live separately from the other hotkeys because
    // it has two a chord hotkey
    e.preventDefault();
    e.stopPropagation();

    const hotkeyScope = singleEnabledScope[0];
    const dealShortId = hotkeyScope.split('-deal-line')[0];
    const deal = deals?.find((deal) => deal.short_id === dealShortId) || null;
    setSelectedDeal(deal);
    setOpenDialog('delivery-date');
  });

  useHotkeys(HOTKEYS.DEAL_SALES_REP_1, (e) => {
    // DEV NOTE: this has to live separately from the other hotkeys because
    // it has two a chord hotkey
    e.preventDefault();
    e.stopPropagation();

    const hotkeyScope = singleEnabledScope[0];
    const dealShortId = hotkeyScope.split('-deal-line')[0];
    const deal = deals?.find((deal) => deal.short_id === dealShortId) || null;
    setSelectedDeal(deal);
    setOpenDialog('sales-rep-1');
  });

  useHotkeys(HOTKEYS.DEAL_SALES_REP_2, (e) => {
    // DEV NOTE: this has to live separately from the other hotkeys because
    // it has two a chord hotkey
    e.preventDefault();
    e.stopPropagation();

    const hotkeyScope = singleEnabledScope[0];
    const dealShortId = hotkeyScope.split('-deal-line')[0];
    const deal = deals?.find((deal) => deal.short_id === dealShortId) || null;
    setSelectedDeal(deal);
    setOpenDialog('sales-rep-2');
  });

  useHotkeys(HOTKEYS.DEAL_FINANCE_MANAGER, (e) => {
    // DEV NOTE: this has to live separately from the other hotkeys because
    // it has two a chord hotkey
    e.preventDefault();
    e.stopPropagation();

    const hotkeyScope = singleEnabledScope[0];
    const dealShortId = hotkeyScope.split('-deal-line')[0];
    const deal = deals?.find((deal) => deal.short_id === dealShortId) || null;
    setSelectedDeal(deal);
    setOpenDialog('finance-manager');
  });

  useHotkeys(HOTKEYS.DEAL_DELIVERY_PREFERENCE, (e) => {
    // DEV NOTE: this has to live separately from the other hotkeys because
    // it has two a chord hotkey
    e.preventDefault();
    e.stopPropagation();

    const hotkeyScope = singleEnabledScope[0];
    const dealShortId = hotkeyScope.split('-deal-line')[0];
    const deal = deals?.find((deal) => deal.short_id === dealShortId) || null;
    setSelectedDeal(deal);
    setOpenDialog('delivery-preference');
  });

  useHotkeys(HOTKEYS.DEAL_DELIVERY_QUEUE, (e) => {
    // DEV NOTE: this has to live separately from the other hotkeys because
    // it has two a chord hotkey
    e.preventDefault();
    e.stopPropagation();

    const hotkeyScope = singleEnabledScope[0];
    const dealShortId = hotkeyScope.split('-deal-line')[0];
    const deal = deals?.find((deal) => deal.short_id === dealShortId) || null;
    setSelectedDeal(deal);
    setOpenDialog('delivery-queue');
  });

  useHotkeys(HOTKEYS.DEAL_LENDER, (e) => {
    // DEV NOTE: this has to live separately from the other hotkeys because
    // it has two a chord hotkey
    e.preventDefault();
    e.stopPropagation();

    const hotkeyScope = singleEnabledScope[0];
    const dealShortId = hotkeyScope.split('-deal-line')[0];
    const deal = deals?.find((deal) => deal.short_id === dealShortId) || null;
    setSelectedDeal(deal);
    setOpenDialog('set-lender');
  });

  return {
    openDialog,
    setOpenDialog,
    selectedDeal,
    setSelectedDeal,
    enableScope,
    singleEnabledScope,
  };
};

export default useAllListAndBoardHotkeys;
